import { CaretDownIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useId, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { Collapse } from "@/components/Collapse/Collapse";
import { Footer } from "@/components/Footer/Footer";
import { IconHeadphones, IconWhatsApp } from "@/components/Icons";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import { Tab, Tabs } from "@/components/Tabs";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { WHATSAPP_URL } from "@/utils/constants";

export const Route = createFileRoute("/_authenticated/chat")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <main className="main">
      <div className="space-y-8 p-main">
        <ContactSupportSection />

        <FaqSection />
      </div>

      <Footer />
    </main>
  );
}

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

function Section({ title, children }: SectionProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-text-title">{title}</h2>

      <div className="flex flex-col items-center gap-4">{children}</div>
    </div>
  );
}

function ContactSupportSection() {
  const intl = useIntl();

  return (
    <Section
      title={intl.formatMessage({
        defaultMessage: "Help",
        description: "Title of support section",
      })}
    >
      <IconHeadphones className="size-[100px]" />

      <div className="space-y-2 text-center">
        <p className="text-lg font-semibold text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Need help booking your trip?",
            description:
              "Support section question asking if the user needs help with booking their trip",
          })}
        </p>

        <p className="text-base text-text-secondary">
          {intl.formatMessage({
            defaultMessage:
              "Contact us and we will help you complete your booking as quickly as possible.",
            description:
              "Support section description providing information on how users can get help with their booking",
          })}
        </p>
      </div>

      <Button
        onClick={() => {
          rudderAnalytics.track(CommonTrackingEvents.USER_CONTACTED_SUPPORT);
        }}
        href={WHATSAPP_URL}
        variant="contained"
        color="whatsapp"
        startIcon={<IconWhatsApp className="size-5" />}
      >
        {intl.formatMessage({
          defaultMessage: "Chat on WhatsApp",
          description: "Button label to open WhatsApp chat with support",
        })}
      </Button>
    </Section>
  );
}

function FaqSection() {
  const intl = useIntl();
  const { locale } = useLocaleInfo();

  const [currentTab, setCurrentTab] = useState<"flight" | "hotel">("flight");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["faqItems", locale],
    queryFn: async () => {
      let mod;
      if (locale === "en") {
        mod = await import(`../../../data/faq-en.json`);
      } else {
        mod = await import(`../../../data/faq-ar.json`);
      }

      return mod.default;
    },
    select: (response) => response[currentTab],
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isError) {
      Sentry.captureException(error);
    }
  }, [isError, error]);

  if (isError) return null;

  return (
    <Section
      title={intl.formatMessage({
        defaultMessage: "FAQ",
        description: "Title of the FAQ section",
      })}
    >
      <Tabs value={currentTab} onChange={setCurrentTab}>
        <Tab
          value="flight"
          label={intl.formatMessage({
            defaultMessage: "Flight",
            description: "Tab label for the Flights FAQ section",
          })}
        />
        <Tab
          value="hotel"
          label={intl.formatMessage({
            defaultMessage: "Hotel",
            description: "Tab label for the Hotels FAQ section",
          })}
        />
      </Tabs>

      <div className="flex w-full flex-col gap-2">
        {isLoading &&
          Array.from({ length: 7 }).map((_, index) => (
            <Skeleton key={index} dimmed height={54} />
          ))}

        {data?.map((item) => {
          return (
            <FaqItem
              key={item.question}
              question={item.question}
              answer={item.answer}
            />
          );
        })}
      </div>
    </Section>
  );
}

type FaqItemData = {
  question: string;
  answer: string;
};

function FaqItem({ question, answer }: FaqItemData) {
  const collapseId = useId();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="section-card rounded-2xl border border-stroke-neutral-soft p-0">
      <button
        className="flex w-full items-center justify-between gap-2 px-3 py-4"
        aria-controls={collapseId}
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen(!isOpen);

          if (!isOpen) {
            rudderAnalytics.track(CommonTrackingEvents.USER_CLICKED_FAQ_ITEM, {
              question,
            });
          }
        }}
      >
        <span className="text-start text-sm font-medium text-text-primary">
          {question}
        </span>

        <CaretDownIcon
          size={20}
          className={clsx("transition-transform duration-200", {
            "rotate-180": isOpen,
          })}
        />
      </button>

      <Collapse id={collapseId} in={isOpen}>
        <div className="px-3 pb-3">
          <span className="text-justify text-xs text-text-primary">
            {answer}
          </span>
        </div>
      </Collapse>
    </div>
  );
}
